import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTournaments, getTrainingSessions, registerForTournament, registerForTraining, cancelTrainingRegistration, getAttendance } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Trophy, Calendar, Users, Clock, MapPin, CalendarCheck, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

const TerminePage = () => {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const fetchData = async () => {
    try {
      const [tRes, sRes, aRes] = await Promise.all([
        getTournaments(), getTrainingSessions(), getAttendance(month, year)
      ]);
      setTournaments(tRes.data);
      setSessions(sRes.data);
      setAttendance(aRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [month, year]);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const handleRegisterTournament = async (id) => {
    setRegistering(id);
    try {
      await registerForTournament(id);
      toast.success('Erfolgreich angemeldet!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setRegistering(null); }
  };

  const handleRegisterTraining = async (id) => {
    setRegistering(id);
    try {
      await registerForTraining(id);
      toast.success('Erfolgreich angemeldet!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setRegistering(null); }
  };

  const handleCancelTraining = async (id) => {
    setRegistering(id);
    try {
      await cancelTrainingRegistration(id);
      toast.success('Abgemeldet');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setRegistering(null); }
  };

  const best = attendance?.monthly_best;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="termine-page">
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            <span className="text-primary">Termine</span> & Aktivitäten
          </h1>
          <p className="text-text-secondary max-w-2xl">Turniere, Training und Anwesenheit auf einen Blick.</p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-max">
          <Tabs defaultValue="turniere" className="space-y-6">
            <TabsList className="bg-surface-elevated">
              <TabsTrigger value="turniere">Turniere</TabsTrigger>
              <TabsTrigger value="training">Training</TabsTrigger>
              <TabsTrigger value="anwesenheit">Anwesenheit</TabsTrigger>
            </TabsList>

            {/* TURNIERE */}
            <TabsContent value="turniere">
              {loading ? (
                <div className="space-y-4">{[1,2].map(i=><Card key={i} className="card-surface animate-pulse"><CardContent className="p-6"><div className="h-20 bg-white/10 rounded"/></CardContent></Card>)}</div>
              ) : tournaments.length > 0 ? (
                <div className="space-y-4">
                  {tournaments.map(t => (
                    <Card key={t.tournament_id} className="card-surface hover:border-primary/30 transition-all" data-testid={`tournament-${t.tournament_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            {t.image_url ? (
                              <img src={getPhotoUrl(t.image_url)} alt="" className="w-16 h-16 object-cover rounded-sm flex-shrink-0" />
                            ) : (
                              <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                <Trophy className="w-7 h-7 text-primary" />
                              </div>
                            )}
                            <div>
                              <h3 className="text-lg font-heading font-bold uppercase">{t.name}</h3>
                              <div className="flex flex-wrap items-center gap-3 text-sm text-text-secondary mt-1">
                                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(t.date)}</span>
                                {t.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.location}</span>}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <Users className="w-3.5 h-3.5 text-accent" />
                                <span className="text-sm text-accent">{t.current_participants || 0} Anmeldungen</span>
                              </div>
                            </div>
                          </div>
                          <Button className="btn-primary flex-shrink-0" onClick={() => handleRegisterTournament(t.tournament_id)} disabled={registering === t.tournament_id}>
                            {registering === t.tournament_id ? 'Wird angemeldet...' : 'Anmelden'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="card-surface"><CardContent className="p-8 text-center"><Trophy className="w-12 h-12 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Keine Turniere geplant.</p></CardContent></Card>
              )}
            </TabsContent>

            {/* TRAINING */}
            <TabsContent value="training">
              {loading ? (
                <div className="space-y-4">{[1,2].map(i=><Card key={i} className="card-surface animate-pulse"><CardContent className="p-6"><div className="h-20 bg-white/10 rounded"/></CardContent></Card>)}</div>
              ) : sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map(s => (
                    <Card key={s.session_id} className="card-surface" data-testid={`training-${s.session_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-accent/10 rounded-sm flex flex-col items-center justify-center flex-shrink-0">
                              <span className="text-lg font-heading font-bold text-accent">{new Date(s.date).getDate()}</span>
                              <span className="text-xs text-text-secondary">{new Date(s.date).toLocaleDateString('de-DE',{month:'short'})}</span>
                            </div>
                            <div>
                              <h3 className="font-heading font-bold uppercase">{s.description || 'Training'}</h3>
                              <div className="flex items-center gap-3 text-sm text-text-secondary mt-1">
                                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{s.time} Uhr</span>
                                <span>{s.duration_minutes} Min.</span>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <Users className="w-3.5 h-3.5 text-accent" />
                                <span className="text-sm text-accent">{s.current_participants || 0}/{s.max_participants || 12}</span>
                              </div>
                            </div>
                          </div>
                          <Button className="btn-primary flex-shrink-0" onClick={() => handleRegisterTraining(s.session_id)} disabled={registering === s.session_id}>
                            Anmelden
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="card-surface"><CardContent className="p-8 text-center"><Calendar className="w-12 h-12 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Keine Trainings geplant.</p></CardContent></Card>
              )}
            </TabsContent>

            {/* ANWESENHEIT */}
            <TabsContent value="anwesenheit">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button variant="ghost" size="sm" onClick={() => { if (month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1); }}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="font-heading font-bold uppercase text-lg">{MONTHS[month-1]} {year}</span>
                <Button variant="ghost" size="sm" onClick={() => { if (month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1); }}><ChevronRight className="w-4 h-4" /></Button>
              </div>

              {best && (
                <Card className="card-elevated border-yellow-500/30 mb-6">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center"><Trophy className="w-6 h-6 text-yellow-500" /></div>
                    <div>
                      <p className="text-sm text-yellow-400 font-bold uppercase">Meiste Anwesenheit im {MONTHS[month-1]}</p>
                      <p className="text-lg font-heading font-bold">{best.name} — {best.count} Teilnahmen</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {attendance?.members?.length > 0 ? (
                <div className="space-y-2">
                  {attendance.members.map((m, i) => (
                    <div key={m.user_id} className={`card-surface p-4 rounded-lg flex items-center justify-between ${best && m.user_id === best.user_id && m.count > 0 ? 'border border-yellow-500/30' : ''}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-text-secondary text-sm w-6 text-right">{i+1}.</span>
                        <span className={`font-medium ${best && m.user_id === best.user_id && m.count > 0 ? 'text-yellow-400' : ''}`}>{m.name}</span>
                      </div>
                      <Badge variant="outline">{m.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <Card className="card-surface"><CardContent className="p-8 text-center"><CalendarCheck className="w-12 h-12 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Keine Daten für diesen Monat.</p></CardContent></Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default TerminePage;
